import { formatApiError } from "@/lib/api";
import { resolveSatJobError } from "@/lib/satAuditErrors";

const SAT_ALERT_CODES = new Set([
  "SAT_PENDIENTE",
  "SAT_UNAVAILABLE",
  "SAT_COOLDOWN",
  "SAT_SOLICITUD_RECHAZADA",
  "SAT_DOWNLOAD_IN_PROGRESS",
  "SAT_ERROR",
  "SAT_WORKER_ERROR",
]);

const SPECIFIC_CODES = new Set([
  "SAT_SOLICITUD_RECHAZADA",
  "SAT_CREDENTIALS",
  "INVALID_FIEL",
  "INVALID_FIEL_SECRET",
  "FIEL_NOT_STORED",
  "FIEL_IS_CSD",
  "SAT_BATCH_ALL_FAILED",
  "SAT_DOC_DOWNLOAD_FAILED",
  "AUTO_SYNC_DISABLED",
]);

/**
 * Normaliza errores de descarga SAT CFDI para toast o AlertDialog.
 */
export function resolveSatDownloadFailure(error) {
  const rawDetail = error?.response?.data?.detail;
  const jobLike = error?.job || (rawDetail && typeof rawDetail === "object" ? { error: rawDetail } : null);
  const parsed = jobLike ? resolveSatJobError(jobLike) : null;

  const detail = rawDetail;
  const code = parsed?.code || (detail && typeof detail === "object" ? String(detail.code || "") : "");
  const status = error?.response?.status;
  let msg = parsed?.technicalSummary || parsed?.userMessage || formatApiError(detail);

  if (!msg) {
    if (error?.code === "ECONNABORTED" || /timeout/i.test(String(error?.message || ""))) {
      msg = "La descarga tardó demasiado sin respuesta del servidor o del SAT.";
    } else if (error?.code === "ERR_NETWORK" || !error?.response) {
      msg = "No hubo respuesta del servidor. Verifique que el backend siga activo y su conexión a Internet.";
    } else {
      msg = error?.message || "Error en la descarga automática";
    }
  }

  const isTimeout = error?.code === "ECONNABORTED" || /timeout/i.test(String(error?.message || ""));
  const isNetwork = error?.code === "ERR_NETWORK" || (!error?.response && Boolean(error));

  const hasSpecificSatHint = Boolean(parsed?.satCodigo || parsed?.satHint || parsed?.failurePhase);
  const showSatAlert =
    !hasSpecificSatHint &&
    (SAT_ALERT_CODES.has(code) ||
      status === 502 ||
      status === 503 ||
      status === 429 ||
      status === 409 ||
      isTimeout ||
      isNetwork);

  let title = "El SAT está teniendo problemas de conexión";
  if (code === "SAT_COOLDOWN" || status === 429) {
    title = "Solicitud SAT reciente";
  } else if (code === "SAT_DOWNLOADS_PAUSED" || status === 423) {
    title = "Descargas SAT pausadas";
  } else if (code === "SAT_PENDIENTE" || status === 503) {
    title = "El SAT sigue procesando la solicitud";
  } else if (code === "SAT_DOWNLOAD_IN_PROGRESS" || status === 409) {
    title = "Descarga SAT en curso";
  } else if (code === "AUTO_SYNC_DISABLED") {
    title = "Sincronización SAT requerida";
  } else if (SPECIFIC_CODES.has(code) || hasSpecificSatHint) {
    title = "El SAT rechazó la solicitud";
  } else if (isTimeout || isNetwork) {
    title = "No se pudo completar la descarga";
  }

  let message;
  if (parsed?.userMessage && (SPECIFIC_CODES.has(code) || hasSpecificSatHint)) {
    message = parsed.userMessage;
  } else if (code === "SAT_COOLDOWN" || status === 429) {
    message =
      "Ya se envió una solicitud para este cliente y periodo. Espere como mínimo 15 minutos antes de intentar de nuevo.";
  } else if (code === "SAT_DOWNLOADS_PAUSED" || status === 423) {
    message =
      "Las descargas SAT están pausadas para este cliente mientras se revisa la cola. Reactive cuando termine la investigación.";
  } else if (code === "SAT_PENDIENTE" || status === 503) {
    message =
      "El SAT aún procesa los comprobantes de este periodo. Espere como mínimo 15 minutos antes de volver a intentar.";
  } else if (code === "SAT_DOWNLOAD_IN_PROGRESS" || status === 409) {
    message =
      "Ya hay una descarga SAT en curso para este cliente. Si se interrumpió por Internet o un cierre inesperado, use «Liberar descarga trabada» en esta pantalla.";
  } else if (code === "AUTO_SYNC_DISABLED") {
    message =
      "Active la sincronización automática SAT en Clientes → Sincronización SAT (con fecha de inicio y e.Firma) antes de usar la descarga manual.";
  } else if (isTimeout) {
    message = "La operación superó el tiempo de espera. Reintente en 15–30 minutos.";
  } else if (isNetwork) {
    message = "No se recibió respuesta del servidor.";
  } else if (hasSpecificSatHint) {
    message = parsed.userMessage || "Revise el detalle técnico del SAT.";
  } else {
    message =
      "El servicio del SAT no respondió correctamente. Espere 15 minutos antes de reintentar.";
  }

  const detailLine = parsed?.technicalSummary || msg;

  return {
    showSatAlert,
    title,
    message,
    detail: detailLine,
    technicalSummary: parsed?.technicalSummary || "",
    satHint: parsed?.satHint,
    failurePhase: parsed?.failurePhase,
    satCodigo: parsed?.satCodigo,
    code,
  };
}

/** Timeout cliente (ms): un poco menor que el límite del backend para recibir JSON de error. */
export const SAT_DOWNLOAD_CLIENT_TIMEOUT_MS = 480000;
